import { NgModule } from "@angular/core";
import { RouterModule, Routes } from "@angular/router";
import counsellingRoutes from "@modules/counselling/counselling.routes";
import faceScanRoutes from "@modules/face-scan/face-scan.routes";
import healthCheckRoutes from "@modules/health-check/health-check.routes";
import healthScreeningRoutes from "@modules/health-screening/health-screening.routes";
import laboratoryRoutes from "@modules/laboratory/laboratory.routes";
import libraryRoutes from "@modules/library/library.routes";
import pulseSurveyRoutes from "@modules/pulse-survey/pulse-survey.routes";
import trainingRoutes from "@modules/training/training.routes";
import wpaRoutes from "@modules/wpa/wpa.routes";
import { requiresSalubiPlus } from "@shared/guards/salubi-plus.guard";
import { MainLayoutComponent } from "./layout/main-layout/main-layout.component";
import { hasDoneMultipleThings } from "./pages/home/home.guard";
import { authGuard } from "./shared/guards/auth.guard";
import whistleblowerRoutes from "./whistleblower/whistleblower.routes";

const routes: Routes = [
	{
		path: "survey/:surveyId/transition",
		loadComponent: () =>
			import(
				"./survey/survey-group-transition/survey-group-transition.component"
			).then((m) => m.SurveyGroupTransitionComponent),
	},
	{
		path: "survey/options",
		loadComponent: () =>
			import("./legacy_survey/surveyoptions/surveyoptions.component").then(
				(m) => m.SurveyoptionsComponent,
			),
	},
	{
		path: "survey/:surveyId",
		loadComponent: () =>
			import("./survey/surveyquestions/survey-questions.component").then(
				(m) => m.SurveyQuestionsComponent,
			),
	},
	{
		path: "",
		loadComponent: () =>
			import("@layout/clean-layout/clean-layout.component").then(
				(m) => m.CleanLayoutComponent,
			),
		children: [
			{
				path: "invalid",
				loadComponent: () =>
					import("./login/invalid/invalid.component").then(
						(m) => m.InvalidComponent,
					),
			},
			{
				path: "userinfo",
				loadComponent: () =>
					import("./login/userinfo/userinfo.component").then(
						(m) => m.UserinfoComponent,
					),
			},
			{
				path: "survey/:surveyId/introduction",
				loadComponent: () =>
					import("./survey/survey/survey.component").then(
						(m) => m.SurveyComponent,
					),
			},
			{ path: "", redirectTo: "/home", pathMatch: "full" },
			//{ path: 'home', component: SurveyComponent },
			{
				path: "password/reset",
				loadComponent: () =>
					import("./login/password-reset/password-reset.component").then(
						(m) => m.PasswordResetComponent,
					),
			},
			{
				path: "password/reset/confirm",
				loadComponent: () =>
					import(
						"./login/password-reset-confirm/password-reset-confirm.component"
					).then((m) => m.PasswordResetConfirmComponent),
			},
			{
				path: "booking/cancellation/:id",
				loadComponent: () =>
					import(
						"./pages/booking-cancellation/booking-cancellation.component"
					).then((m) => m.BookingCancellationComponent),
				canActivate: [authGuard],
			},
			{
				path: "login",
				loadComponent: () =>
					import("./login/login/login.component").then((m) => m.LoginComponent),
			},
			{
				path: "language",
				loadComponent: () =>
					import("./login/lang/lang.component").then((m) => m.LangComponent),
			},
			{
				path: "language/:languageCode",
				loadComponent: () =>
					import("./login/lang/lang.component").then((m) => m.LangComponent),
			},
			{
				path: "consent",
				loadComponent: () =>
					import("./login/consent/consent.component").then(
						(m) => m.ConsentComponent,
					),
			},
			{
				path: "link/face-scan/:userId",
				loadComponent: () =>
					import("./login/lang/lang.component").then((m) => m.LangComponent),
			},
			{
				path: "login/booking",
				loadComponent: () =>
					import("./login/login/login.component").then((m) => m.LoginComponent),
			},
			{
				path: "login/apv",
				loadComponent: () =>
					import("./login/login/login.component").then((m) => m.LoginComponent),
			},
			{
				path: "login/face-scan",
				loadComponent: () =>
					import("./login/login/login.component").then((m) => m.LoginComponent),
			},
			{
				path: "login/hcbooking",
				loadComponent: () =>
					import("./login/login/login.component").then((m) => m.LoginComponent),
			},
			{
				path: "login/hcbooking-manage",
				loadComponent: () =>
					import("./login/login/login.component").then((m) => m.LoginComponent),
			},
			{
				path: "login/:haserror",
				loadComponent: () =>
					import("./login/login/login.component").then((m) => m.LoginComponent),
			},
			{
				path: "userlogin",
				loadComponent: () =>
					import("./login/userlogin/userlogin.component").then(
						(m) => m.UserloginComponent,
					),
			},
			{
				path: "userlogin/:userId",
				loadComponent: () =>
					import("./login/userlogin/userlogin.component").then(
						(m) => m.UserloginComponent,
					),
			},
			{
				path: "userlogin/:userId/:ticket",
				loadComponent: () =>
					import("./login/userlogin/userlogin.component").then(
						(m) => m.UserloginComponent,
					),
			},
			{
				path: "vicorda/:userId",
				loadComponent: () =>
					import("./login/nologin/nologin.component").then(
						(m) => m.NologinComponent,
					),
			},
			{
				path: "ias/:userId/:lang",
				loadComponent: () =>
					import("./login/nologin/nologin.component").then(
						(m) => m.NologinComponent,
					),
			},
			{
				path: "ias/history/:lang/:userId",
				loadComponent: () =>
					import("./login/historylogin/historylogin.component").then(
						(m) => m.HistoryloginComponent,
					),
			},
			{
				path: "link/:userId/survey/:surveyId",
				loadComponent: () =>
					import("./login/lang/lang.component").then((m) => m.LangComponent),
			},
			{
				path: "link/:userId/survey/:surveyId/adminkey/:adminkey",
				loadComponent: () =>
					import("./login/lang/lang.component").then((m) => m.LangComponent),
			},
			{
				path: "link/:userId/survey/:surveyId/adminkey/:adminkey/lang/:lang",
				loadComponent: () =>
					import("./login/lang/lang.component").then((m) => m.LangComponent),
			},
			{
				path: "link/:userId/followup/:surveyId",
				loadComponent: () =>
					import("./login/lang/lang.component").then((m) => m.LangComponent),
			},
			{
				path: "link/:userId/auth/:adminAuthTicket/subscription/:showsubscription/subId/:subid/productId/:productid/OrderId/:orderid",
				loadComponent: () =>
					import("./login/lang/lang.component").then((m) => m.LangComponent),
			},
			{
				path: "link/:userId/auth/:adminAuthTicket",
				loadComponent: () =>
					import("./login/lang/lang.component").then((m) => m.LangComponent),
			},

			{
				path: "link/:userId",
				loadComponent: () =>
					import("./login/lang/lang.component").then((m) => m.LangComponent),
			},

			{
				path: "link/healthcheck/:userId",
				loadComponent: () =>
					import("./login/lang/lang.component").then((m) => m.LangComponent),
			},
			{
				path: "link/mobile",
				loadComponent: () =>
					import("./login/lang/lang.component").then((m) => m.LangComponent),
			},
			{
				path: "link/booking/:userId",
				loadComponent: () =>
					import("./login/lang/lang.component").then((m) => m.LangComponent),
			},
			{
				path: "survey/grouptype/:typeId/:groupId",
				loadComponent: () =>
					import("./legacy_survey/surveygroup/surveygroup.component").then(
						(m) => m.SurveygroupComponent,
					),
			},
			{
				path: "survey/group/:groupId",
				loadComponent: () =>
					import("./legacy_survey/surveygroup/surveygroup.component").then(
						(m) => m.SurveygroupComponent,
					),
			},
			{
				path: "last",
				loadComponent: () =>
					import("./legacy_survey/surveylast/surveylast.component").then(
						(m) => m.SurveylastComponent,
					),
			},
			{
				path: "survey/response/recommendations",
				loadComponent: () =>
					import(
						"./legacy_survey/surveyrecommendations/surveyrecommendations.component"
					).then((m) => m.SurveyrecommendationsComponent),
			},
			{
				path: "survey/create/user",
				loadComponent: () =>
					import(
						"./legacy_survey/surveycreateuser/surveycreateuser.component"
					).then((m) => m.SurveycreateuserComponent),
			},
			{
				path: "welcome",
				loadComponent: () =>
					import("./pages/welcome/welcome.component").then(
						(m) => m.WelcomeComponent,
					),
			},
			{
				path: "salubi-home",
				loadComponent: () =>
					import("./pages/salubi-home/salubi-home.component").then(
						(m) => m.SalubiHomeComponent,
					),
			},
			{
				path: "noreport",
				loadComponent: () =>
					import("./survey/surveynoreport/surveynoreport.component").then(
						(m) => m.SurveynoreportComponent,
					),
			},
			{
				path: "video/:exerciseId",
				loadComponent: () =>
					import("./video/trainingvideo/trainingvideo.component").then(
						(m) => m.TrainingvideoComponent,
					),
			},
			{
				path: "reportVideo/:videoname",
				loadComponent: () =>
					import(
						"./video/trainingreportvideo/trainingreportvideo.component"
					).then((m) => m.TrainingreportvideoComponent),
			},
			{
				path: "criiptologin",
				loadComponent: () =>
					import("./login/criiptologin/criiptologin.component").then(
						(m) => m.CriiptologinComponent,
					),
			},
			{
				path: "link/healthcheck/:userId/adminkey/:adminkey/practitioner/:practitioner",
				loadComponent: () =>
					import("./login/lang/lang.component").then((m) => m.LangComponent),
			},
			{
				path: "home",
				loadComponent: () =>
					import("./pages/home/home.component").then((m) => m.HomeComponent),
				canActivate: [authGuard],
			},
			{
				path: "supervision-plan",
				loadComponent: () =>
					import("./pages/supervision-plan/supervision-plan.component").then(
						(m) => m.SupervisionPlanComponent,
					),
				canActivate: [authGuard],
			},
			{
				path: "my-health",
				loadComponent: () =>
					import("./pages/my-health/my-health.component").then(
						(m) => m.MyHealthComponent,
					),
				canActivate: [authGuard],
			},
			{
				path: "medicine-guide",
				loadComponent: () =>
					import("./pages/medicine-guide/medicine-guide.component").then(
						(m) => m.MedicineGuideComponent,
					),
				canActivate: [authGuard, requiresSalubiPlus],
			},
			{
				path: "help",
				loadComponent: () =>
					import("./pages/help/help.component").then((m) => m.HelpComponent),
			},
			{
				path: "salubi-plus",
				loadComponent: () =>
					import("./pages/salubi-plus/salubi-plus.component").then(
						(m) => m.SalubiPlusComponent,
					),
				canActivate: [authGuard],
			},
			{
				path: "partner-discounts",
				loadComponent: () =>
					import("./pages/partner-discounts/partner-discounts.component").then(
						(m) => m.PartnerDiscountsComponent,
					),
				canActivate: [authGuard, requiresSalubiPlus],
			},
			{
				path: "partner-discounts/:slug",
				loadComponent: () =>
					import(
						"./pages/partner-discounts/category/partner-category.component"
					).then((m) => m.PartnerCategoryComponent),
				canActivate: [authGuard, requiresSalubiPlus],
			},
			{
				path: "book-consultation",
				loadComponent: () =>
					import(
						"./pages/help/book-consultation/book-consultation.component"
					).then((m) => m.BookConsultationComponent),
			},
			{
				path: "services",
				loadComponent: () =>
					import("./pages/services/services.component").then(
						(m) => m.ServicesComponent,
					),
				canActivate: [authGuard],
			},
			{
				path: "webshop",
				loadComponent: () =>
					import("./pages/webshop/webshop.component").then(
						(m) => m.WebshopComponent,
					),
				canActivate: [authGuard],
			},
			{
				path: "cart",
				loadComponent: () =>
					import("./pages/cart/cart.component").then((m) => m.CartComponent),
				canActivate: [authGuard],
			},
			{
				path: "order-confirmation",
				loadComponent: () =>
					import(
						"./pages/order-confirmation/order-confirmation.component"
					).then((m) => m.OrderConfirmationComponent),
				canActivate: [authGuard],
			},
			{
				path: "profile",
				loadComponent: () =>
					import("./pages/profile/profile/profile.component").then(
						(m) => m.ProfileComponent,
					),
				canActivate: [authGuard],
			},

			{
				path: "survey/:id",
				loadComponent: () =>
					import("./legacy_survey/survey/survey.component").then(
						(m) => m.SurveyComponent,
					),
			},
			{
				path: "profile/consents",
				loadComponent: () =>
					import(
						"./pages/profile/profile-consents/profile-consents.component"
					).then((m) => m.ProfileConsentsComponent),
				canActivate: [authGuard],
			},
			{
				path: "subscriptions",
				loadComponent: () =>
					import("./pages/subscriptions/subscriptions.component").then(
						(m) => m.SubscriptionsComponent,
					),
				canActivate: [authGuard],
			},
			{
				path: "order-history",
				loadComponent: () =>
					import("./pages/order-history/order-history.component").then(
						(m) => m.OrderHistoryComponent,
					),
				canActivate: [authGuard],
			},

			// Health Check Booking
			{
				path: "booking",
				loadChildren: () =>
					import(
						"./modules/health-check-booking/health-check-booking.module"
					).then((m) => m.HealthCheckBookingModule),
			},
			{
				path: "booking/:userId",
				loadComponent: () =>
					import("./pages/booking-view/booking-view.component").then(
						(m) => m.BookingViewComponent,
					),
				canActivate: [authGuard],
			},
			{
				path: "booking/:userId/manage",
				loadComponent: () =>
					import("./pages/booking-manage/booking-manage.component").then(
						(m) => m.BookingManageComponent,
					),
				canActivate: [authGuard],
			},
			{
				path: "screeningonly/:userId/survey/:surveyId",
				loadComponent: () =>
					import("./login/lang/lang.component").then((m) => m.LangComponent),
			},
		],
	},
	{
		path: "notifications",
		component: MainLayoutComponent,
		children: [
			{
				path: "",
				loadComponent: () =>
					import(
						"./pages/notifications/notifications/notifications.component"
					).then((m) => m.NotificationsComponent),
			},
			{
				path: ":notificationType",
				loadComponent: () =>
					import(
						"./pages/notifications/notifications/notifications.component"
					).then((m) => m.NotificationsComponent),
			},
			{
				path: "message/:id",
				loadComponent: () =>
					import(
						"./pages/notifications/notifications-message/notifications-message.component"
					).then((m) => m.NotificationsMessageComponent),
			},
		],
	},
	{
		path: "whistleblowerlight",
		component: MainLayoutComponent,
		children: [
			{
				path: "",
				loadComponent: () =>
					import(
						"./whistleblower/whistleblower-light-index/whistleblower-light-index.component"
					).then((m) => m.WhistleblowerLightIndexComponent),
			},
		],
	},
	...whistleblowerRoutes,
	...wpaRoutes,
	...faceScanRoutes,
	...healthScreeningRoutes,
	...healthCheckRoutes,
	...counsellingRoutes,
	...laboratoryRoutes,
	...libraryRoutes,
	...pulseSurveyRoutes,
	...trainingRoutes,
];

@NgModule({
	imports: [
		RouterModule.forRoot(routes, {
			scrollPositionRestoration: "enabled",
		}),
	],
	exports: [RouterModule],
})
export class AppRoutingModule {}
